import React, { useMemo } from "react";
import useApi from "../nerdlets/home/useApi";
import { getApiPaths } from "../nerdlets/home/constants/healthEndpoints";
import { STORE_MAIN_SERVICES } from "../nerdlets/home/cardsConfig";
import { currentMarket } from "./market";

/**
 * Per-service traffic lights for the clicked store.
 *
 * The same services the market's Service Roll-up shows, read from the same
 * endpoint (`RMPWidgetStoresView`) so the two cannot disagree — the roll-up is
 * this, counted across every store.
 *
 * Only the four services the API actually reports per store are here. The
 * roll-up also lists Network and Infrastructure, but those are literals injected
 * by useApi for the market-level services view, not readings — a green light for
 * them would be invented, and an invented green on an operations dashboard is
 * worse than a gap nobody can mistake for a measurement.
 */

/**
 * A service's state for one store, reading both shapes the API has served:
 * the top-level status object, and the alerts array under `services`. Either
 * one reporting trouble is trouble — neither is treated as authoritative on its
 * own, because both have been present at once and disagreed.
 */
function readService(row, key) {
  const status = row[key] && typeof row[key] === "object" ? row[key] : null;

  // `serviceIssues` currently mirrors `services` exactly — every one of the 18
  // impacted PT stores had both, identical. Read both anyway in case only one
  // is populated, but deduplicate: concatenating reported every alert twice.
  const alerts = [
    ...new Map(
      [row.services?.[key], row.serviceIssues?.[key]]
        .filter(Array.isArray)
        .flat()
        .map((alert) => [
          `${alert?.alert}|${alert?.condition}|${alert?.detail}|${alert?.guid}`,
          alert,
        ])
    ).values(),
  ];

  // Silence is health: the payload lists only what is wrong. CH proves it —
  // its rows carry a `services` map holding just the services with alerts and
  // no per-service status objects at all, so every healthy service is absent.
  // (PT's rows carry the status objects too. Same endpoint, same hour.)
  if (!status && !alerts.length) return { state: "ok", text: "OK" };

  const colour = String(status?.color || "").toLowerCase();
  const down = String(status?.status || "").toLowerCase() === "down";

  if (colour === "red" || down || alerts.length) {
    const issue = status?.issue || alerts[0]?.alert || "Issue reported";
    const count = alerts.length || status?.count || 0;
    return {
      state: "bad",
      text: count > 1 ? `${issue} (×${count})` : issue,
    };
  }

  if (colour === "amber") {
    return { state: "warn", text: status?.issue || "Degraded" };
  }

  return { state: "ok", text: "OK" };
}

export default function StoreServices({ storeId }) {
  const accountId = useMemo(() => currentMarket()?.accountId ?? null, []);
  const paths = useMemo(
    () => (accountId ? getApiPaths(accountId) : null),
    [accountId]
  );

  // useApi starts at loading:false with data:null, so "no data and no error
  // yet" is the pending test, not `loading`.
  const { data, error } = useApi(paths?.STORES_VIEW);

  const row = useMemo(() => {
    if (!Array.isArray(data) || !storeId) return null;
    const wanted = String(storeId);
    return (
      data.find((r) => String(r.store) === wanted) ||
      // Store ids are zero-padded in the payload; a caller may not pad.
      data.find((r) => Number(r.store) === Number(wanted)) ||
      null
    );
  }, [data, storeId]);

  // No market in the URL means nothing to look the store up in. Say so rather
  // than rendering six confident lights with no data behind them.
  const pending = !error && accountId && !data;

  // Absence of a *service* means healthy; absence of the whole row does not.
  // A failed request, no market to read from, or a store the view does not
  // list leaves us with nothing to report — which must not read as six greens.
  const unreadable = !pending && !row;

  const services = STORE_MAIN_SERVICES.map((service) => ({
    key: service.key,
    label: service.label,
    ...(pending
      ? { state: "pending", text: "…" }
      : unreadable
      ? { state: "unknown", text: "No reading" }
      : readService(row, service.key)),
  }));

  return (
    <section className="entity-section">
      <h3>Store services</h3>

      <ul className="store-services" aria-busy={pending ? "true" : undefined}>
        {services.map((service) => (
          <li className="store-services__item" key={service.key}>
            <span
              className={`store-light store-light--${service.state}`}
              aria-hidden="true"
            />
            <span className="store-services__name">{service.label}</span>
            {/* Truncated to one line by CSS; the title carries the rest, since
                alert names run long enough to wrap the rail into a mess. */}
            <span
              className={`store-services__state store-services__state--${service.state}`}
              title={service.text}
            >
              {service.text}
            </span>
          </li>
        ))}
      </ul>

      {error ? (
        <p className="entity-panel__muted" role="alert">
          Service health unavailable: {error.message}
        </p>
      ) : null}

      {!error && !accountId ? (
        <p className="entity-panel__muted">No market in view to read from.</p>
      ) : null}

      {!error && accountId && data && !row ? (
        <p className="entity-panel__muted">
          Store {storeId} is not in this market&apos;s stores view.
        </p>
      ) : null}
    </section>
  );
}
